// client/src/hooks/useLeagueStandings.js
import { useState, useEffect, useCallback } from 'react'
import {
  calculateLeagueStandings,
  groupStandingsByDivision,
  getPlayerRank,
} from '../utils/standings'
import { services } from '../services'
import { MINIMUM_MATCHES_FOR_ELIGIBILITY } from '../utils/pointsCalculation'

/**
 * Custom hook to manage league standings data fetching and processing
 * @param {string} leagueId - The league ID to fetch standings for
 * @returns {Object} Hook state containing loading, error, and processed standings data
 */
function useLeagueStandings(leagueId) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [leagueData, setLeagueData] = useState(null)
  const [currentPlayerId, setCurrentPlayerId] = useState(null)
  const [overallStandings, setOverallStandings] = useState([])
  const [divisionStandings, setDivisionStandings] = useState({})
  const [currentPlayerRank, setCurrentPlayerRank] = useState(-1)
  const [selectedLeagueLevelId, setSelectedLeagueLevelId] = useState(null)

  const loadLeagueStandings = useCallback(async () => {
    let shouldKeepLoading = false

    try {
      setLoading(true)
      setError(null)

      // Use the league standings service method
      const response = await services.league.getLeagueStandings(
        leagueId,
        selectedLeagueLevelId,
      )

      if (!response.success) {
        throw new Error('Failed to load league standings data')
      }

      const data = response.data

      // If this is the first (unfiltered) load and the league has multiple
      // levels, default to the first active level. The resulting state change
      // will re-trigger this callback with the correct level already set,
      // so only the scoped call surfaces data to the UI.
      if (data.active_levels?.length > 0 && !selectedLeagueLevelId) {
        shouldKeepLoading = true
        setSelectedLeagueLevelId(data.active_levels[0].id)
        return
      }

      setLeagueData(data)
      setCurrentPlayerId(data.currentPlayerId)

      // Process the data
      const matchFormat = data.matchFormat || 'TRADITIONAL'
      const minimumMatchesForEligibility =
        data.playoffMinMatchesRequired || MINIMUM_MATCHES_FOR_ELIGIBILITY
      const standings = calculateLeagueStandings(
        data.allPlayers,
        data.allMatches,
        data.currentPlayerId,
        null,
        matchFormat,
        minimumMatchesForEligibility,
      )
      setOverallStandings(standings)

      // Group standings by division
      const grouped = groupStandingsByDivision(standings, matchFormat)
      setDivisionStandings(grouped)

      // Get current player's rank
      const rank = getPlayerRank(standings, data.currentPlayerId)
      setCurrentPlayerRank(rank)
    } catch (err) {
      const errorMessage = 'Unable to load league standings.'
      setError(errorMessage)
      console.error('League standings error:', err)
    } finally {
      if (!shouldKeepLoading) {
        setLoading(false)
      }
    }
  }, [leagueId, selectedLeagueLevelId])

  useEffect(() => {
    if (!leagueId) {
      // Resolve to idle state when no leagueId is provided
      setLoading(false)
      return
    }

    loadLeagueStandings()
  }, [leagueId, loadLeagueStandings])

  return {
    loading,
    error,
    leagueData,
    currentPlayerId,
    overallStandings,
    divisionStandings,
    currentPlayerRank,
    selectedLeagueLevelId,
    setSelectedLeagueLevelId,
    refetch: loadLeagueStandings,
  }
}

export default useLeagueStandings
